"use client";

import { Fragment, ReactNode } from "react";
import GTMEventLink from "~/components/ui/GTMEventLink";
import { getFooterLinkEventData } from "~/lib/gtm/getGtmEventData";
import LogoIcon from "@/icons/brand/logo.svg";
import { cn } from "~/lib/utils";
import FooterLegalLinks from "./FooterLegalLinks";
import FooterCopyright from "./FooterCopyright";
import FooterMenuLink from "./FooterMenuLink";
import LegitScriptBadge from "~/componentsND/LegitScriptBadge";
import { footerMenus, footerSocialLinks } from "~/data/footer";
import { FOOTER_LINK_TEXT_AA } from "~/constants/flags";
import { useFlag } from "~/hooks/useFlag";

interface BaseFooterProps {
    topContent?: ReactNode;
    sideContent?: ReactNode;
  renderLegitScriptInsideMenu?: boolean;
  logoClassName?: string;
}

const BaseFooter = ({
    topContent,
    sideContent,
    renderLegitScriptInsideMenu,
    logoClassName,
}: BaseFooterProps) => {
    const isLinkTextVariant = !!useFlag(FOOTER_LINK_TEXT_AA);

    return (
        <footer className="footer relative z-20 w-full bg-heroBlue rounded-t-[24px] lg:rounded-t-[48px] p-6 pt-12 lg:p-12 font-[Helvetica]">
            <div className="flex flex-wrap max-lg:gap-10 justify-between w-full">
                {topContent}

                {footerMenus.map((sections, index) => (
                    <div
                        key={index}
                        className={cn(
                            index > 0
                                ? "max-md:w-[42%] max-md:grow"
                                : "max-md:w-full max-md:flex max-md:gap-10"
                        )}
                    >
                        {sections.map((section, idx) => (
                            <Fragment key={idx}>
                                <div className={cn(index > 0 ? "" : "max-md:w-[42%] max-md:grow", idx > 0 && "mt-8")}>
                                    <span className="block font-platform font-medium text-sm text-white leading-none uppercase tracking-[1.3px] opacity-60 mb-4">
                                        {section.text}
                                    </span>

                                    <ul className="flex flex-col gap-4">
                                        {section.items?.map((item, idx) => (
                                            <li key={`${idx}_${item.text}`}>
                                                <FooterMenuLink
                                                    href={item.href}
                                                    target={item.target}
                                                    className="block text-base text-white leading-normal"
                                                    handleClick={item.handleClick}
                                                    preventDefault={item.preventDefault}
                                                    gtmEventData={getFooterLinkEventData(item.text)}
                                                >
                                                    {isLinkTextVariant && item.variantText ? item.variantText : item.text}
                                                </FooterMenuLink>
                                            </li>
                                        ))}
                                    </ul>

                                    {renderLegitScriptInsideMenu && section.support && (
                                        <div className="max-lg:hidden mt-10">
                                            <LegitScriptBadge />
                                        </div>
                                    )}
                                </div>

                                {section.followUs && (
                                    <div className={cn(index > 0 ? "" : "max-md:w-[42%] max-md:grow")}>
                                        <span className="block font-platform font-medium text-sm text-white leading-none uppercase tracking-[1.3px] opacity-60 mb-2 md:mt-8">
                                            FOLLOW US
                                        </span>
                                        <div className="flex gap-[4px] items-center">
                                            {footerSocialLinks.map((link, idx) => (
                                                <GTMEventLink
                                                    key={idx}
                                                    href={link.href}
                                                    className="footer-social-icon"
                                                    title={link.title}
                                                    target="_blank"
                                                    gtmEventData={getFooterLinkEventData(link.title)}
                                                >
                                                    {link.icon}
                                                </GTMEventLink>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Fragment>
                        ))}
                    </div>
                ))}

                {sideContent}
            </div>

            <LogoIcon
                className={cn(
                    "block w-full h-auto mt-10 mb-10 lg:mb-8",
                    logoClassName
                )}
                width="1920"
                fill="#fff"
            />

            <div className="flex md:flex-row-reverse max-md:flex-col justify-between gap-6 md:gap-2 pt-10 border-t-2 border-t-[#FFFFFF33]">
                <FooterLegalLinks />
                <FooterCopyright />
            </div>
        </footer>
    );
};

export default BaseFooter;
